import { DateTime, Duration } from "luxon";
import { isEmpty } from "lodash-es";
import {
  AudioRecordingAppEvent,
  AudioRecordingAppEventKind,
  AudioRecordingModelData,
  RecordingDurationFailureReason,
} from "@/store/audio-recordings/types";
import type { Uuid } from "@/domains/global/identifiers";

/**
 * Minimum duration for a recording to be considered valid.
 * Recordings shorter than this threshold are considered "too short" and may not
 * have captured meaningful audio content. If a recording is longer than this
 * but has no finalized local artifact, it indicates an actual storage failure
 * rather than user error.
 */
export const MIN_RECORDING_DURATION = Duration.fromObject({ seconds: 5 });

/**
 * Result of calculating recording duration from app_events.
 */
export type RecordingDurationResult =
  | { success: true; duration: Duration; startedAt: DateTime; completedAt: DateTime }
  | { success: false; reason: RecordingDurationFailureReason };

export const NO_AUDIO_CONTENT_DETECTED_ERROR_MESSAGE = "No audio content detected";
export const RECORDING_AUDIO_FAILED_TO_SAVE_ERROR_MESSAGE = "Recording audio failed to save";

export type MissingAudioContentFailureInfo = {
  errorMessage: string;
  isTooShort: boolean;
  durationResult: RecordingDurationResult;
};

export type PersistedSilenceSummary = {
  hasDetectedSound: boolean;
  observedDurationMs: number | null;
};

export const MEETING_HANDOFF_PREVIOUS_AUDIO_RECORDING_ID_PROPERTY =
  "meeting_handoff_previous_audio_recording_id";
export const MEETING_HANDOFF_SUCCESSOR_AUDIO_RECORDING_ID_PROPERTY =
  "meeting_handoff_successor_audio_recording_id";
export const MEETING_HANDOFF_PIPELINE_DEFERRED_PROPERTY = "meeting_handoff_pipeline_deferred";

type PersistedCompletedArtifactSummary = {
  sizeBytes: number | null;
  partCount: number | null;
  hasEmptyArtifact: boolean | null;
};

type PersistedCompletedTranscriptSummary = {
  transcriptTurnCount: number | null;
};

/**
 * Typed accessor for app_events from AudioRecordingModelData.
 * The value_json field is loosely typed, so this provides type safety.
 */
export function getAppEvents(modelData: AudioRecordingModelData): AudioRecordingAppEvent[] {
  const valueJson = modelData.value_json as { app_events?: AudioRecordingAppEvent[] } | undefined;
  return valueJson?.app_events ?? [];
}

/**
 * Find the first event of a specific kind from app_events.
 */
export function findAppEvent(
  appEvents: AudioRecordingAppEvent[],
  kind: AudioRecordingAppEventKind
): AudioRecordingAppEvent | undefined {
  return appEvents.find(event => event.kind === kind);
}

/**
 * Find the most recent event of a specific kind from app_events.
 */
export function findLatestAppEvent(
  appEvents: AudioRecordingAppEvent[],
  kind: AudioRecordingAppEventKind
): AudioRecordingAppEvent | undefined {
  for (let index = appEvents.length - 1; index >= 0; index -= 1) {
    if (appEvents[index]?.kind === kind) {
      return appEvents[index];
    }
  }

  return undefined;
}

/**
 * Returns the recording segment that was replaced by this detected-meeting handoff segment.
 */
export function getMeetingHandoffPreviousAudioRecordingId({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): Uuid | null {
  const event = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.STARTED_LOCAL
  );
  const value = event?.properties?.[MEETING_HANDOFF_PREVIOUS_AUDIO_RECORDING_ID_PROPERTY];
  return typeof value === "string" ? (value as Uuid) : null;
}

/**
 * Returns the successor recording that owns processing for this deferred handoff segment.
 */
export function getMeetingHandoffSuccessorAudioRecordingId({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): Uuid | null {
  const event = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.COMPLETED_LOCAL
  );
  const value = event?.properties?.[MEETING_HANDOFF_SUCCESSOR_AUDIO_RECORDING_ID_PROPERTY];
  return typeof value === "string" ? (value as Uuid) : null;
}

/**
 * True when a superseded recording should upload only after its successor completes.
 */
export function isPipelineDeferredToMeetingHandoffSuccessor({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): boolean {
  const event = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.COMPLETED_LOCAL
  );

  return (
    event?.properties?.[MEETING_HANDOFF_PIPELINE_DEFERRED_PROPERTY] === true &&
    getMeetingHandoffSuccessorAudioRecordingId({ modelData }) !== null
  );
}

/**
 * Parse an ISO datetime string to a Luxon DateTime object.
 * Returns null if parsing fails or results in an invalid DateTime.
 */
function parseDateTime(isoString: string): DateTime | null {
  const dt = DateTime.fromISO(isoString);
  return dt.isValid ? dt : null;
}

function getDurationMsFromEventProperties(
  event: AudioRecordingAppEvent | undefined
): number | null {
  const durationMs = event?.properties?.duration_ms;

  if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) {
    return null;
  }

  return Math.max(0, Math.round(durationMs));
}

function buildRecordingDurationResultFromExplicitDuration({
  explicitDurationMs,
  startedAt,
  completedAt,
}: {
  explicitDurationMs: number;
  startedAt: DateTime | null;
  completedAt: DateTime | null;
}): RecordingDurationResult {
  if (!startedAt && !completedAt) {
    return { success: false, reason: RecordingDurationFailureReason.INVALID_TIMESTAMPS };
  }

  if (startedAt && completedAt) {
    return {
      success: true,
      duration: Duration.fromObject({ milliseconds: explicitDurationMs }),
      startedAt,
      completedAt,
    };
  }

  if (startedAt) {
    return {
      success: true,
      duration: Duration.fromObject({ milliseconds: explicitDurationMs }),
      startedAt,
      completedAt: startedAt.plus({ milliseconds: explicitDurationMs }),
    };
  }

  if (!completedAt) {
    return { success: false, reason: RecordingDurationFailureReason.INVALID_TIMESTAMPS };
  }

  return {
    success: true,
    duration: Duration.fromObject({ milliseconds: explicitDurationMs }),
    startedAt: completedAt.minus({ milliseconds: explicitDurationMs }),
    completedAt,
  };
}

/**
 * Calculate the recording duration from app_events.
 * Uses the occurred_at timestamps from started_local and completed_local events.
 */
export function calculateRecordingDuration({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): RecordingDurationResult {
  const appEvents = getAppEvents(modelData);

  if (isEmpty(appEvents)) {
    return { success: false, reason: RecordingDurationFailureReason.NO_APP_EVENTS };
  }

  const startedEvent = findAppEvent(appEvents, AudioRecordingAppEventKind.STARTED_LOCAL);
  if (!startedEvent) {
    return { success: false, reason: RecordingDurationFailureReason.MISSING_STARTED_EVENT };
  }

  const completedEvent = findAppEvent(appEvents, AudioRecordingAppEventKind.COMPLETED_LOCAL);
  if (!completedEvent) {
    return { success: false, reason: RecordingDurationFailureReason.MISSING_COMPLETED_EVENT };
  }

  const startedAt = parseDateTime(startedEvent.occurred_at);
  const completedAt = parseDateTime(completedEvent.occurred_at);
  const explicitDurationMs = getDurationMsFromEventProperties(completedEvent);

  if (explicitDurationMs !== null) {
    return buildRecordingDurationResultFromExplicitDuration({
      explicitDurationMs,
      startedAt,
      completedAt,
    });
  }

  if (!startedAt || !completedAt) {
    return { success: false, reason: RecordingDurationFailureReason.INVALID_TIMESTAMPS };
  }

  const duration = completedAt.diff(startedAt);
  return { success: true, duration, startedAt, completedAt };
}

/**
 * Check if a recording is too short to have produced any audio content.
 * This happens when the recording duration is less than MIN_RECORDING_DURATION,
 * meaning no audio chunks were captured before the recording was stopped.
 */
export function isRecordingTooShort({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): boolean {
  const { isTooShort } = getMissingAudioContentFailureInfo({ modelData });

  return isTooShort;
}

/**
 * Returns the user-facing error we should show when the recording exists but
 * the durable audio bytes are missing.
 */
export function getMissingAudioContentFailureInfo({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): MissingAudioContentFailureInfo {
  const durationResult = calculateRecordingDuration({ modelData });

  if (!durationResult.success) {
    return {
      errorMessage: RECORDING_AUDIO_FAILED_TO_SAVE_ERROR_MESSAGE,
      isTooShort: false,
      durationResult,
    };
  }

  const isTooShort =
    durationResult.duration.as("milliseconds") < MIN_RECORDING_DURATION.as("milliseconds");

  return {
    errorMessage: isTooShort
      ? NO_AUDIO_CONTENT_DETECTED_ERROR_MESSAGE
      : RECORDING_AUDIO_FAILED_TO_SAVE_ERROR_MESSAGE,
    isTooShort,
    durationResult,
  };
}

/**
 * Returns the persisted silence summary captured on the completed-local app
 * event. This survives refreshes and lets boot recovery preserve the
 * long-but-silent classification even though the live silence probe is
 * in-memory only.
 */
export function getPersistedSilenceSummary({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): PersistedSilenceSummary | null {
  const completedEvent = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.COMPLETED_LOCAL
  );
  const hasDetectedSound = completedEvent?.properties?.silence_has_detected_sound;

  if (typeof hasDetectedSound !== "boolean") {
    return null;
  }

  const observedDurationMsValue = completedEvent?.properties?.silence_observed_duration_ms;
  const observedDurationMs =
    typeof observedDurationMsValue === "number" && Number.isFinite(observedDurationMsValue)
      ? Math.max(0, Math.round(observedDurationMsValue))
      : null;

  return {
    hasDetectedSound,
    observedDurationMs,
  };
}

function getPersistedCompletedArtifactSummary({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): PersistedCompletedArtifactSummary | null {
  const completedEvent = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.COMPLETED_LOCAL
  );
  const artifactSizeBytesValue = completedEvent?.properties?.artifact_size_bytes;
  const sizeBytes =
    typeof artifactSizeBytesValue === "number" && Number.isFinite(artifactSizeBytesValue)
      ? Math.max(0, Math.round(artifactSizeBytesValue))
      : null;
  const artifactPartCountValue = completedEvent?.properties?.artifact_part_count;
  const partCount =
    typeof artifactPartCountValue === "number" && Number.isFinite(artifactPartCountValue)
      ? Math.max(0, Math.round(artifactPartCountValue))
      : null;
  const hasEmptyArtifactValue = completedEvent?.properties?.has_empty_artifact;
  const hasEmptyArtifact =
    typeof hasEmptyArtifactValue === "boolean" ? hasEmptyArtifactValue : null;

  if (sizeBytes === null && partCount === null && hasEmptyArtifact === null) {
    return null;
  }

  return {
    sizeBytes,
    partCount,
    hasEmptyArtifact,
  };
}

export function hasPersistedNonEmptyArtifact({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): boolean {
  const summary = getPersistedCompletedArtifactSummary({ modelData });

  if (!summary) {
    return false;
  }

  if (summary.sizeBytes !== null) {
    return summary.sizeBytes > 0;
  }

  if (summary.partCount !== null) {
    return summary.partCount > 0;
  }

  if (summary.hasEmptyArtifact !== null) {
    return !summary.hasEmptyArtifact;
  }

  return false;
}

function getPersistedCompletedTranscriptSummary({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): PersistedCompletedTranscriptSummary | null {
  const completedEvent = findLatestAppEvent(
    getAppEvents(modelData),
    AudioRecordingAppEventKind.COMPLETED_LOCAL
  );
  const transcriptTurnCountValue = completedEvent?.properties?.transcript_turn_count;
  const transcriptTurnCount =
    typeof transcriptTurnCountValue === "number" && Number.isFinite(transcriptTurnCountValue)
      ? Math.max(0, Math.round(transcriptTurnCountValue))
      : null;

  if (transcriptTurnCount === null) {
    return null;
  }

  return {
    transcriptTurnCount,
  };
}

export function hasPersistedTranscriptEvidence({
  modelData,
}: {
  modelData: AudioRecordingModelData;
}): boolean {
  const summary = getPersistedCompletedTranscriptSummary({ modelData });

  return (
    summary !== null && summary.transcriptTurnCount !== null && summary.transcriptTurnCount > 0
  );
}
